import { Button } from "@/components/ui/button";
import {
  ApplicationSize,
  Revision,
  Status,
} from "@/gen/rbt/cloud/v1alpha1/application/application_pb";
import { cn } from "@/lib/utils";
import { AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

interface RevisionTimelineProps {
  revisions: Revision[];
  className?: string;
  userEmails?: Record<string, string>;
}

// Status badge colors and labels.
const STATUS_CONFIG: Record<number, { label: string; className: string }> = {
  [Status.UNKNOWN]: {
    label: "Unknown",
    className: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  },
  [Status.UPPING]: {
    label: "Deploying",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  },
  [Status.UP]: {
    label: "Active",
    className:
      "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  },
  [Status.DOWNING]: {
    label: "Stopping",
    className:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
  },
  [Status.DOWN]: {
    label: "Stopped",
    className: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  },
  [Status.FAILED]: {
    label: "Failed",
    className: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  },
};

// Helper to format relative time.
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) {
    return "just now";
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

// Human-readable labels for application sizes.
const SIZE_LABELS: Record<number, string> = {
  [ApplicationSize.XSMALL]: "XS",
  [ApplicationSize.SMALL]: "S",
  [ApplicationSize.MEDIUM]: "M",
  [ApplicationSize.LARGE]: "L",
  [ApplicationSize.XLARGE]: "XL",
};

function RevisionStatusBadge({ status }: { status: Status }) {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG[Status.UNKNOWN];
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
        config.className
      )}
    >
      {config.label}
    </span>
  );
}

// Exported for use as an application-level status badge.
export { STATUS_CONFIG, RevisionStatusBadge as StatusBadge };

function RevisionRow({
  revision,
  userEmails,
}: {
  revision: Revision;
  userEmails?: Record<string, string>;
}) {
  const [isFailureOpen, setIsFailureOpen] = useState(false);

  // Extract short SHA256 for display.
  const getShortImageRef = (imageName: string): string => {
    const sha256Match = imageName.match(/@sha256:([a-f0-9]+)/);
    if (sha256Match) {
      return sha256Match[1].slice(0, 12);
    }
    // Fallback: show last part of image name.
    return imageName.split("/").pop() || imageName;
  };
  const shortImageName = getShortImageRef(revision.containerImageName);

  // Convert protobuf Timestamp to Date.
  const deployedAtDate = revision.deployedAt?.toDate();

  return (
    <div
      className={cn(
        "relative pl-6 pb-4 last:pb-0",
        // Timeline line.
        "before:absolute before:left-[7px] before:top-3 before:bottom-0 before:w-px",
        "before:bg-border last:before:hidden"
      )}
    >
      {/* Timeline dot. */}
      <div
        className={cn(
          "absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 bg-background",
          revision.status === Status.UP
            ? "border-green-500"
            : revision.status === Status.FAILED
            ? "border-red-500"
            : "border-muted-foreground/30"
        )}
      />

      {/* Revision content. */}
      <div className="space-y-1">
        {/* Header row: revision number, status, time. */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">
            Rev {Number(revision.number)}
          </span>
          <RevisionStatusBadge status={revision.status} />
          {revision.size !== undefined && (
            <span
              className={cn(
                "inline-flex items-center px-2 py-0.5",
                "rounded text-xs font-medium",
                "bg-purple-100 text-purple-700",
                "dark:bg-purple-900 dark:text-purple-300"
              )}
            >
              {SIZE_LABELS[revision.size] ?? "Unknown"}
            </span>
          )}
          {deployedAtDate && (
            <span
              className="text-xs text-muted-foreground cursor-help"
              title={deployedAtDate.toLocaleString()}
            >
              {formatRelativeTime(deployedAtDate)}
            </span>
          )}
        </div>

        {/* Details row: image, replicas, deployer. */}
        <div className="text-xs text-muted-foreground space-y-0.5">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="bg-muted px-1 rounded">{shortImageName}</code>
            {revision.deployerUserId && (
              <>
                <span>&middot;</span>
                <span>
                  Deployed by{" "}
                  {userEmails?.[revision.deployerUserId] ??
                    revision.deployerUserId}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Failure reason (expandable). */}
        {revision.status === Status.FAILED && revision.failureReason && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-red-600 dark:text-red-400 hover:text-red-700"
              onClick={() => setIsFailureOpen(!isFailureOpen)}
            >
              <AlertCircle className="h-3 w-3 mr-1" />
              View failure details
              {isFailureOpen ? (
                <ChevronUp className="h-3 w-3 ml-1" />
              ) : (
                <ChevronDown className="h-3 w-3 ml-1" />
              )}
            </Button>
            {isFailureOpen && (
              <div className="mt-1 p-2 bg-red-50 dark:bg-red-950 rounded text-xs text-red-700 dark:text-red-300 font-mono">
                {revision.failureReason}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function RevisionTimeline({
  revisions,
  className,
  userEmails,
}: RevisionTimelineProps) {
  // Show revisions in reverse order (most recent first).
  const sortedRevisions = [...revisions].reverse();

  // By default, show only the last 3 revisions.
  const [showAll, setShowAll] = useState(false);
  const visibleRevisions = showAll
    ? sortedRevisions
    : sortedRevisions.slice(0, 3);
  const hiddenCount = sortedRevisions.length - 3;

  return (
    <div className={cn("space-y-2", className)}>
      <h4 className="text-sm font-medium text-muted-foreground">
        Revision History
      </h4>

      <div className="space-y-0">
        {visibleRevisions.map((revision) => (
          <RevisionRow
            key={Number(revision.number)}
            revision={revision}
            userEmails={userEmails}
          />
        ))}
      </div>

      {/* Show more/less toggle. */}
      {hiddenCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? (
            <>
              <ChevronUp className="h-3 w-3 mr-1" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3 mr-1" />
              Show {hiddenCount} more revision
              {hiddenCount !== 1 ? "s" : ""}
            </>
          )}
        </Button>
      )}
    </div>
  );
}
